import { Injector } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { createRedirectionUrlFromNavigationOptions } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { BeneficiariesSuccessCodes, BeneficiaryErrorCodes } from '@app/translations';
import { FormActionTypeEnum, FormID, OtpParam } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ACCOUNTTYPE,
  APP_ZONES,
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  ConfirmationClaimType,
  actionButtonNavigationConfigType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { catchError, take, tap } from 'rxjs';
import { UpdateBeneficiary } from '../../../actions/beneficiary.action';
import { ClearBulkPaymentList, GetBulkPaymentList } from '../../../actions/bulk-payment.action';
import { ClearFormDataActionWithId } from '../../../actions/form-submission.action';
import { AccountService } from '../../../services/account-service/account-service.service';
import {
  AddBeneficiaryRequest,
  BeneficiaryIdentifierAddress,
  UpdateBeneficiaryRequest,
} from '../../../services/account-service/account.type';
import { BackOverrideService } from '../../../services/back-override-service/back-override.service';
import { ConfirmationService } from '../../../services/confirmation-service/confirmation-service';
import { WorkflowFormsService } from '../../../services/workflow-forms/workflow-forms.service';
import { ApiActions, UpdateApiIsLoadingAction } from '../../../state/api-loading.state';
import { FormActionState } from '../../../state/form-submision.state';
import { ProjectSettingsState, ProjectSettingsStateModel } from '../../../state/project-settings.state';
import { RoleState } from '../../../state/role.state';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { getQueryParamValueByKey } from '../../query-params.utils';
import { formatNameAsUrl } from '../../zone-url.utils';
import { mapBeneficiaryFormDataToAddBeneficiaryRequest } from '../beneficiary-form/beneficiary-form-actions.utils';
import { redirectToPage } from '../cta-button-actions.utils';
import { handleRedirection } from '../cta-redirection.utils';

/**
 * Maps OTP parameters to their corresponding form action types for beneficiary operations.
 *
 * This mapping is used to associate specific OTP parameters with form state identifiers
 * that represent different actions related to beneficiaries, such as adding or editing
 * a beneficiary.
 *
 * @type {Object.<string, {otpParam: OtpParam, formStateId: FormActionTypeEnum}>}
 */
const beneficiaryFlowMap: {
  [key: string]: { otpParam: OtpParam; formStateId: FormActionTypeEnum };
} = {
  [OtpParam.AddBeneficiary]: {
    otpParam: OtpParam.AddBeneficiary,
    formStateId: FormActionTypeEnum.ADD_BENEFICIARY,
  },
  [OtpParam.EditBeneficiary]: {
    otpParam: OtpParam.EditBeneficiary,
    formStateId: FormActionTypeEnum.EDIT_BENEFICIARY,
  },
};

/**
 * Handles the addition or editing of a beneficiary based on the provided flow type.
 *
 * This function retrieves necessary services and state data using the provided injector.
 * It determines the beneficiary flow type from query parameters and executes the appropriate
 * action based on the flow type and project settings. If SMS authentication is enabled and
 * the current role is not excluded, it redirects to an OTP page; otherwise, it initiates
 * an API call directly.
 *
 * @param injector - The Angular Injector used to retrieve services and state.
 * @param widgetProperties
 * @param isSwiftPayment
 */
export function addEditBeneficiary(
  injector: Injector,
  widgetProperties?: BaseWidgetProperties,
  isSwiftPayment = false,
) {
  const store = injector.get(Store);
  const route = injector.get(ActivatedRoute);
  const workflowFormService = injector.get(WorkflowFormsService);
  const confirmationService = injector.get(ConfirmationService);

  const projectSettingsData = store.selectSnapshot(ProjectSettingsState.getProjectSettings);
  const roleData = store.selectSnapshot(RoleState.getRole);
  const beneficiaryConfirmationEnabled = beneficiaryRequiresConfirmation(projectSettingsData, roleData?.role);

  const params = route.snapshot.queryParams;
  const beneficiaryFlowType = beneficiaryFlowMap[params['flow']];
  const beneficiaryForm = workflowFormService.getFormPart(FormID.BENEFICIARY_FORM);
  const navigationConfig = widgetProperties?.actionNavigationConfigurations;

  let formData: AddBeneficiaryRequest | UpdateBeneficiaryRequest;

  if (beneficiaryFlowType?.otpParam === OtpParam.BulkPayment) {
    callContinueAnywayAPI(injector);
    return;
  }

  //if using the new beneficiary form flow, the form will have been touched, so we can get the data from there
  //otherwise we get the data from the form action state
  if (beneficiaryForm?.touched && beneficiaryForm?.valid) {
    //isSwiftPayment is only set in the case that the CoP action is being used for both FPS/SEPA and Swift payments,
    // and is set to true in that case in `beneficiary-form-actions.utils.ts` where we use the alternative_1 alternative_2 navigation outcomes, so we need to swap them here
    if (isSwiftPayment && navigationConfig) {
      navigationConfig[ActionButtonOutcomesType.SUCCESS] =
        navigationConfig[ActionButtonOutcomesType.ALTERNATE_1];

      navigationConfig[ActionButtonOutcomesType.FAILURE] =
        navigationConfig[ActionButtonOutcomesType.ALTERNATE_2];
    }
    const isAddressTouched = beneficiaryForm?.get('beneficiaryAddress')?.touched;
    formData = mapBeneficiaryFormDataToAddBeneficiaryRequest(beneficiaryForm, isAddressTouched);
  } else {
    const formActionStateData = store.selectSnapshot(
      FormActionState.getFormActionStateWithId(beneficiaryFlowType.formStateId),
    );
    formData =
      beneficiaryFlowType.otpParam === OtpParam.AddBeneficiary
        ? mapFormActionDataToAddBeneficiaryRequest(formActionStateData?.formData)
        : mapFormActionDataToAddBeneficiaryRequest(formActionStateData?.formData?.payload);
  }

  if (beneficiaryConfirmationEnabled) {
    // need to use confirmation service to handle confirmation
    const loadingAction = ApiActions.addEditDomesticBeneficiary;
    let returnUrl = '/zones/payments/beneficiary-list';
    if (navigationConfig?.[ActionButtonOutcomesType.SUCCESS]) {
      returnUrl = createRedirectionUrlFromNavigationOptions(
        navigationConfig[ActionButtonOutcomesType.SUCCESS],
      );
    }
    // ensure we only take the first confirmation emission and let the observable complete
    confirmationService
      .checkConfirmation(returnUrl, loadingAction)
      .pipe(take(1))
      .subscribe(() => {
        initiateAPICall(injector, formData, widgetProperties as BaseWidgetProperties);
      });
    if ((formData as UpdateBeneficiaryRequest)?.id) {
      // if updating beneficiary, use update beneficiary confirmation claim type
      confirmationService.confirmAction(
        ConfirmationClaimType.UPDATE_BENEFICIARY,
        returnUrl,
        true,
        loadingAction,
      );
    } else {
      confirmationService.confirmAction(
        ConfirmationClaimType.ADD_BENEFICIARY,
        undefined,
        true,
        loadingAction,
      );
    }
  } else {
    // skip authentication
    initiateAPICall(injector, formData, widgetProperties as BaseWidgetProperties);
  }
}

export function beneficiaryRequiresConfirmation(
  projectSettings: ProjectSettingsStateModel,
  role?: string | undefined,
): boolean | undefined {
  const personaSettings = projectSettings.projectConfig?.personaSpecificSettings;
  if (role && personaSettings && Object.prototype.hasOwnProperty.call(personaSettings, role)) {
    return personaSettings[role]?.authentication?.inApp?.beneficiaryConfirmationEnabled;
  }
  return projectSettings?.authentication?.inApp?.beneficiaryConfirmationEnabled;
}

/**
 * Initiates an API call to either update an existing beneficiary or add a new one.
 *
 * @param injector - The Angular injector used to retrieve services.
 * @param formData - The data for the beneficiary, which can be for adding or updating.
 * @param widgetProperties - Optional widget properties for navigation configuration.
 *
 * If the formData contains an 'id', it dispatches an UpdateBeneficiary action.
 * Otherwise, it calls the API to add a new beneficiary.
 */
export function initiateAPICall(
  injector: Injector,
  formData: AddBeneficiaryRequest | UpdateBeneficiaryRequest,
  widgetProperties: BaseWidgetProperties,
) {
  const navigationConfig = widgetProperties?.actionNavigationConfigurations;
  (formData as UpdateBeneficiaryRequest)?.id
    ? handleBeneficiaryUpdate(injector, formData as UpdateBeneficiaryRequest, widgetProperties)
    : callAddBeneficiaryAPI(injector, formData as AddBeneficiaryRequest, navigationConfig);
}

export const handleBeneficiaryUpdate = (
  injector: Injector,
  formValues: UpdateBeneficiaryRequest,
  widgetProperties: BaseWidgetProperties,
) => {
  const navigationConfig = widgetProperties?.actionNavigationConfigurations;
  const store = injector.get(Store);
  const accessToken = getQueryParamValueByKey('confirmation');

  // Dispatch the action and pass a callback to receive the real API response (or null on failure).
  store
    .dispatch(
      new UpdateBeneficiary(
        formValues,
        formValues?.id || '',
        accessToken,
        navigationConfig,
        (apiResponse) => {
          if (apiResponse) {
            handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.SUCCESS);
          } else {
            // On failure navigate using handleRedirection (provide fallback zone/page for redirectToPage)
            handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.FAILURE);
          }
        },
      ),
    )
    .pipe(take(1))
    .subscribe();
};

/**
 * Calls the add beneficiary API using the provided form data and handles the response.
 *
 * @param injector - The Angular Injector used to retrieve service instances.
 * @param formData - The form data containing beneficiary details to be added.
 * @param navigationConfig
 */
export function callAddBeneficiaryAPI(
  injector: Injector,
  formData: AddBeneficiaryRequest,
  navigationConfig?: actionButtonNavigationConfigType,
) {
  const accountService = injector.get(AccountService);
  const router = injector.get(Router);
  const alertHandlerService = injector.get(AlertHandlerService);
  const backService = injector.get(BackOverrideService);
  const store = injector.get(Store);
  const workflowFormService = injector.get(WorkflowFormsService);

  const accessToken = getQueryParamValueByKey('confirmation');
  const mockWidgetProperties: BaseWidgetProperties = {
    textContent: '',
    zoneToNavigate: '',
    urlToNavigate: '',
  };

  //check if its a COP/VOP flow, so check currency and rail, then set the success/failure or alternate_1 alternate_2
  accountService
    .addBeneficiary(formData, accessToken)
    .pipe(
      take(1),
      tap(() => {
        mockWidgetProperties['urlToNavigate'] =
          navigationConfig?.[ActionButtonOutcomesType.SUCCESS]?.selectedPageUrl ?? `select-beneficiary`;
        mockWidgetProperties['zoneToNavigate'] =
          navigationConfig?.[ActionButtonOutcomesType.SUCCESS]?.selectedZone ?? APP_ZONES.PAYMENT;
        const nextRoute = `/zones/${formatNameAsUrl(mockWidgetProperties['zoneToNavigate'])}/${
          mockWidgetProperties['urlToNavigate']
        }`;
        store.dispatch(new UpdateApiIsLoadingAction(ApiActions.addEditDomesticBeneficiary, false));
        redirectToPage(router, mockWidgetProperties, injector);
        backService.setBackOverride('/zones/landing/home', nextRoute, FormActionTypeEnum.ADD_BENEFICIARY);
        findAndShowAlertFromCode(store, router, alertHandlerService, [
          BeneficiariesSuccessCodes.ADD_BENEFICIARY,
        ]);
        store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.ADD_BENEFICIARY));
        workflowFormService.clearFormFromId(FormID.BENEFICIARY_FORM);
      }),
      catchError((err) => {
        store.dispatch(new UpdateApiIsLoadingAction(ApiActions.addEditDomesticBeneficiary, false));
        findAndShowAlertFromCode(store, router, alertHandlerService, [
          BeneficiaryErrorCodes.FAILED_TO_CREATE_BENEFICIARY,
        ]);

        mockWidgetProperties['urlToNavigate'] =
          navigationConfig?.[ActionButtonOutcomesType.FAILURE]?.selectedPageUrl ??
          formData.accountType === ACCOUNTTYPE.PERSONAL
            ? `add-beneficiary#personal`
            : `add-beneficiary#business`;
        mockWidgetProperties['zoneToNavigate'] =
          navigationConfig?.[ActionButtonOutcomesType.FAILURE]?.selectedZone ?? APP_ZONES.PAYMENT;
        redirectToPage(router, mockWidgetProperties, injector);
        store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.ADD_BENEFICIARY));
        workflowFormService.clearFormFromId(FormID.BENEFICIARY_FORM);
        return err;
      }),
    )
    .subscribe();
}

const callContinueAnywayAPI = (injector: Injector) => {
  const accountService = injector.get(AccountService);
  const router = injector.get(Router);
  const alertHandlerService = injector.get(AlertHandlerService);
  const backService = injector.get(BackOverrideService);
  const store = injector.get(Store);
  const formActionData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.BULK_PAYMENTS),
  );
  const masterFileId = formActionData?.formData.masterFileId;
  const transactionId = formActionData?.formData.transactionId;
  const mockWidgetProperties: BaseWidgetProperties = {
    textContent: '',
    zoneToNavigate: APP_ZONES.PAYMENT,
    urlToNavigate: '',
  };

  // passing suggestion: false as we call the continue anyway function here
  accountService
    .bulkPaymentContinueAnywayAndWithSuggestion(masterFileId, transactionId, { suggestion: false })
    .pipe(
      take(1),
      tap(() => {
        store.dispatch(new ClearBulkPaymentList());
        store.dispatch(new GetBulkPaymentList(1, 10, masterFileId));
        mockWidgetProperties['urlToNavigate'] = `bulk-payment-beneficiary-review`;
        redirectToPage(router, mockWidgetProperties, injector);
        const nextRoute = `/zones/${formatNameAsUrl(mockWidgetProperties['zoneToNavigate'])}/${
          mockWidgetProperties['urlToNavigate']
        }`;
        backService.setBackOverride('/zones/landing/home', nextRoute, FormActionTypeEnum.ADD_BENEFICIARY);
        alertHandlerService.showAlertFn('success', 'Updated Successfully.');
      }),
      catchError((error) => {
        return error;
      }),
    )
    .subscribe();
};

export function mapFormActionDataToAddBeneficiaryRequest(
  form: any,
): AddBeneficiaryRequest | UpdateBeneficiaryRequest {
  // Common fields
  const baseData = {
    name: form?.name ?? '',
    accountType: form?.accountType,
    accountId: form?.accountId,
    destinationIdentifier: form?.destinationIdentifier,
    copStatus: form?.copStatus,
  };

  // Address mapping
  const address = {} as BeneficiaryIdentifierAddress;
  if (form?.addressStreet) address.addressLine1 = form.addressStreet;
  if (form?.addressLine1) address.addressLine1 = form.addressLine1;
  if (form?.addressLine2) address.addressLine2 = form.addressLine2;
  if (form?.addressLine3) address.addressLine3 = form.addressLine3;
  if (form?.addressLine4) address.addressLine4 = form.addressLine4;
  if (form?.addressCity) address.city = form.addressCity;
  if (form?.addressCountry) address.country = form.addressCountry;
  if (form?.addressPostCode) address.postCode = form.addressPostCode;
  if (form?.addressState) address.state = form.addressState;
  if (Object.keys(address).length > 0) {
    (baseData as any).address = address;
  }

  // If both id and bankId are present, return UpdateBeneficiaryRequest
  if (form?.id && form?.bankId) {
    return {
      ...baseData,
      id: form.id,
      bankId: form.bankId,
    } as UpdateBeneficiaryRequest;
  }

  // Otherwise, return AddBeneficiaryRequest (without id and bankId)
  return baseData as AddBeneficiaryRequest;
}

export function getCountryCodeFromBeneficiaryAccountDetail(type: 'iban' | 'bic', value: string): string {
  const splittedValue = value.split('');
  if (type === 'iban') {
    return splittedValue[0] + splittedValue[1];
  }
  if (type === 'bic') {
    return splittedValue[4] + splittedValue[5];
  }
  return '';
}
